import React, { useState, useEffect } from 'react';
import { Project } from '../types/api';
import { ContactApi } from '../services/contactApi';
import ProjectCard from './ProjectCard';

const ProjectsSection: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTech, setSelectedTech] = useState<string>('All');

    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedProjects = await ContactApi.getProjects();
            setProjects(fetchedProjects);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    // Get unique technologies for filtering
    const allTechnologies = React.useMemo(() => {
        const techSet = new Set<string>();
        projects.forEach(project => {
            project.technologies.forEach(tech => techSet.add(tech));
        });
        return ['All', ...Array.from(techSet).sort()];
    }, [projects]);

    // Filter projects by selected technology
    const filteredProjects = React.useMemo(() => {
        if (selectedTech === 'All') return projects;
        return projects.filter(project =>
            project.technologies.includes(selectedTech)
        );
    }, [projects, selectedTech]);

    if (loading) {
        return (
            <div className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-300">Loading projects...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                            <svg className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Projects</h3>
                            <p className="text-red-700 mb-4">{error}</p>
                            <button
                                onClick={loadProjects}
                                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="py-16 bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        My Projects
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        Here are some of the projects I've worked on. Each one represents a unique challenge
                        and showcases different technologies and problem-solving approaches.
                    </p>
                </div>

                {/* Technology Filter */}
                {allTechnologies.length > 1 && (
                    <div className="mb-8">
                        <div className="flex flex-wrap justify-center gap-2">
                            {allTechnologies.map((tech) => (
                                <button
                                    key={tech}
                                    onClick={() => setSelectedTech(tech)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                                        selectedTech === tech
                                            ? 'bg-primary text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                                    }`}
                                >
                                    {tech}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Projects Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.map((project) => (
                        <ProjectCard key={project.id} project={project} />
                    ))}
                </div>

                {/* No projects message */}
                {filteredProjects.length === 0 && selectedTech !== 'All' && (
                    <div className="text-center py-12">
                        <p className="text-gray-600 dark:text-gray-300">
                            No projects found with the technology "{selectedTech}".
                        </p>
                        <button
                            onClick={() => setSelectedTech('All')}
                            className="mt-2 text-primary hover:text-blue-700 transition-colors duration-200"
                        >
                            Show all projects
                        </button>
                    </div>
                )}

                {/* Empty state */}
                {projects.length === 0 && !loading && !error && (
                    <div className="text-center py-12">
                        <p className="text-gray-600 dark:text-gray-300">
                            No projects available at the moment.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectsSection;
